type ColorPalette = {
  [key: string]: string;
};

interface AppColor {
  dataBssThemeDark: ColorPalette;
  dataMenuColorLight: ColorPalette;
  dataBssThemeDarkMenuColorLight: ColorPalette;
  dataTopbarColorLight: ColorPalette;
  dataTopbarColorDark: ColorPalette;
  dataBsThemeLight: ColorPalette;
  dataBsThemeDark: ColorPalette;
  dataCardColorLight: ColorPalette;
  dataCardColorDark: ColorPalette;
  iconButtonLight: ColorPalette;
  iconButtonDark: ColorPalette;
  orderListPropLight: ColorPalette;
  orderListPropDark: ColorPalette;
}

const lightThemeColor = JSON.parse(localStorage.getItem("lightThemeColor") || "{}");
const darkThemeColor = JSON.parse(localStorage.getItem("darkThemeColor") || "{}");

/**
 * GT Brokers brand defaults.
 *
 * Every value below is still a *fallback*: an installation that has saved
 * colours in Setting → Theme Color keeps them, because each entry reads
 * `configured || GT_DEFAULT`. What changed in the redesign is the default —
 * from the inherited POS teal (#298076) to a brokerage navy/blue system.
 *
 * The set is deliberately small. Premium enterprise software gets its
 * character from typography, spacing and restraint, not from a wide palette.
 */
export const GT = {
  /** Deep navy — the sidebar, and the darkest surface in the product. */
  navy: "#0B1B2E",
  navyRaised: "#122539",
  /** Brokerage blue — primary actions, links, active states. */
  blue: "#0F5CAB",
  blueDeep: "#0B4685",
  blueSoft: "#E8F0FA",
  /** Cyan accent — used sparingly, for the active nav marker and chart ramps. */
  cyan: "#3FA9D6",
  cyanLight: "#8ECBF0",
  /** Neutrals, light mode. */
  page: "#F3F6FB",
  surface: "#FFFFFF",
  surfaceSunken: "#F7F9FC",
  border: "#E2E8F1",
  textStrong: "#14212F",
  textMuted: "#5B6B80",
  /** Neutrals, dark mode. */
  pageDark: "#0C1622",
  surfaceDark: "#151F2D",
  surfaceSunkenDark: "#1B2736",
  borderDark: "#26344A",
  textStrongDark: "#E9EEF5",
  textMutedDark: "#95A6BB",
  /**
   * Premium accents.
   *
   * Indigo and violet did not exist in the system before, which is the root
   * cause of the "everything is grey and blue" reading: `--bs-secondary` — the
   * colour MUI hands to every `secondary` surface, and the one the Reports and
   * Tasks module accents resolve through — was defaulting to `textMuted`, a
   * grey. A module asking for "indigo" was literally being given grey.
   */
  indigo: "#4F46E5",
  indigoDeep: "#3730A3",
  indigoLight: "#8B85F0",
  violet: "#7C3AED",
  violetDeep: "#5B21B6",
  violetLight: "#A78BFA",
  teal: "#0D9488",
  tealLight: "#5EEAD4",

  /**
   * Semantic — richer than before, still nowhere near fluorescent.
   *
   * The previous values (#12855B, #B27512, #C24141) were desaturated enough
   * that a "success" chip and a "danger" chip read as two shades of mud at a
   * glance. These are the same hues with the saturation the brand asks for,
   * checked to stay legible as text on their own soft tints.
   */
  success: "#059669",
  successDeep: "#047857",
  successDark: "#34D399",
  warning: "#D97706",
  warningDeep: "#B45309",
  warningDark: "#FBBF24",
  danger: "#E11D48",
  dangerDeep: "#BE123C",
  dangerDark: "#FB7185",
  cyanDeep: "#0E7490",
};

export const appColors: AppColor = {
  iconButtonLight: {
    "editButtonColor":  lightThemeColor?.editButtonColor || lightThemeColor?.primary || GT.blue,
    "editIconColor":  lightThemeColor?.editIconColor || "#ffffff",
    "viewButtonColor":  lightThemeColor?.viewButtonColor || lightThemeColor?.primary || GT.blue,
    "viewIconColor":  lightThemeColor?.viewIconColor || "#ffffff",
    "deleteButtonColor":  lightThemeColor?.deleteButtonColor || lightThemeColor?.primary || GT.blue,
    "deleteIconColor":  lightThemeColor?.deleteIconColor || "#ffffff",
    "addButtonColor":  lightThemeColor?.addButtonColor || lightThemeColor?.primary || GT.blue,
    "addIconColor":  lightThemeColor?.addIconColor || "#ffffff",

  },
  iconButtonDark: {
    "editButtonColor": darkThemeColor?.editButtonColor || darkThemeColor?.primary || GT.cyan,
    "editIconColor": darkThemeColor?.editIconColor || "#ffffff",
    "viewButtonColor": darkThemeColor?.viewButtonColor || darkThemeColor?.primary || GT.cyan,
    "viewIconColor": darkThemeColor?.viewIconColor || "#ffffff",
    "deleteButtonColor": darkThemeColor?.deleteButtonColor || darkThemeColor?.primary || GT.cyan,
    "deleteIconColor": darkThemeColor?.deleteIconColor || "#ffffff",
    "addButtonColor": darkThemeColor?.addButtonColor || darkThemeColor?.primary || GT.cyan,
    "addIconColor": darkThemeColor?.addIconColor || "#ffffff",
  },
  orderListPropLight: {
    "leftBgColor": lightThemeColor?.orderListProp?.leftBgColor || "#ffffff",
    "lable1BgColor": lightThemeColor?.orderListProp?.lable1BgColor || "#ffffff",
    "lable2BgColor": lightThemeColor?.orderListProp?.lable2BgColor || "#ffffff",
    "lable1FontColor": lightThemeColor?.orderListProp?.lable1FontColor || "#ffffff",
    "lable2FontColor": lightThemeColor?.orderListProp?.lable2FontColor || "#eaf3f2",
    "lable1buttonIconColor": lightThemeColor?.orderListProp?.lable1buttonIconColor || "#599d95",
    "headingColor": lightThemeColor?.orderListProp?.headingColor || "#639182",
    "lable2buttonIconColor": lightThemeColor?.orderListProp?.lable2buttonIconColor || "#eaf3f2",
    "lable2buttonbgColor": lightThemeColor?.orderListProp?.lable2buttonbgColor || "#e8f0f3",
    "payButtonColor": lightThemeColor?.orderListProp?.payButtonColor || "#298076",
    "lable1buttonbgColor": lightThemeColor?.orderListProp?.lable1buttonbgColor  || "#85858f",
    "rightFontColor": lightThemeColor?.orderListProp?.rightFontColor || "#9a9da1",
  },
  orderListPropDark: {
    "leftBgColor": darkThemeColor?.orderListProp?.leftBgColor || "#1c2b41",
    "lable1BgColor": darkThemeColor?.orderListProp?.lable1BgColor || "#1c2b41",
    "lable2BgColor": darkThemeColor?.orderListProp?.lable2BgColor || "#1c2b41",
    "lable1FontColor": darkThemeColor?.orderListProp?.lable1FontColor || "#1c2b41",
    "lable2FontColor": darkThemeColor?.orderListProp?.lable2FontColor || "#22272b",
    "lable1buttonIconColor": darkThemeColor?.orderListProp?.lable1buttonIconColor || "#b6c2cf",
    "headingColor": darkThemeColor?.orderListProp?.headingColor || "#a3aeba",
    "lable2buttonIconColor": darkThemeColor?.orderListProp?.lable2buttonIconColor || "#616467",
    "lable2buttonbgColor": darkThemeColor?.orderListProp?.lable2buttonbgColor || "#9f8fef",
    "payButtonColor": darkThemeColor?.orderListProp?.payButtonColor || "#c55827",
    "lable1buttonbgColor": darkThemeColor?.orderListProp?.lable1buttonbgColor  || "#b3bfcc",
    "rightFontColor": darkThemeColor?.orderListProp?.rightFontColor || "#b3bfcc",

  },
  dataBssThemeDark: {
    "--bs-border-color": GT.borderDark,
    "--bs-light": GT.surfaceSunkenDark,
    "--bs-dark": "#f1f1f1",
    "--bs-light-rgb": "243, 246, 251",
    "--bs-dark-rgb": "27, 39, 54",
    "--bs-headings-color": GT.textStrongDark,
    "--bs-input-border-color": GT.borderDark,
    "--bs-input-focus-border-color": GT.cyan,
  },
  dataMenuColorLight: {
    /* The navigation rail is the one place the brand navy appears at full
       strength. It stays navy in both modes — a sidebar that flips to white in
       light mode loses the anchor the whole layout is composed against. */
    "--bs-main-nav-bg": lightThemeColor?.drawer || GT.navy,

    "--bs-main-nav-item-color": "#9fb0c4",
    "--bs-main-nav-item-hover-bg": "transparent",
    /* Drives the active-item accent bar, wash and icon. */
    "--bs-main-nav-item-hover-color": GT.cyanLight,
    "--bs-main-nav-border-color": "#1b2c42",
  },
  dataBssThemeDarkMenuColorLight: {
    "--bs-main-nav-bg": darkThemeColor?.drawer || GT.navy,

    "--bs-main-nav-item-color": "#9fb0c4",
    "--bs-main-nav-item-hover-bg": "transparent",
    "--bs-main-nav-item-hover-color": GT.cyanLight,
    "--bs-main-nav-border-color": "#1b2c42",
  },
  dataTopbarColorLight: {
    "--bs-topbar-bg":  lightThemeColor?.topBar || GT.surface,
    "--bs-heading-color": lightThemeColor?.heading || GT.textStrong,
    /* The drawer header shares the rail navy, so the logo block reads as the
       top of the sidebar rather than as an offcut of the topbar. */
    "--bs-drawer-header-color": lightThemeColor?.drawerHeader || GT.navy,
    "--bs-drawer-color": GT.surface,
    "topBarHeading":  lightThemeColor?.topBarHeading || GT.textStrong,
    /* Section labels sit quieter than items; items sit just under white. */
    "drawerHeadingText":  lightThemeColor?.drawerHeadingText || "#8FA3BC",
    "drawerMenuText":  lightThemeColor?.drawerMenuText || "#CBD8E8",
   



    "--bs-topbar-item-color": GT.textMuted,
    "--bs-topbar-search-bg": GT.surfaceSunken,
  },
  dataTopbarColorDark: {
    "--bs-topbar-bg": darkThemeColor?.topBar || GT.surfaceDark,
    "--bs-heading-color": darkThemeColor?.heading || GT.textStrongDark,
    "--bs-drawer-header-color": darkThemeColor?.drawerHeader || GT.navy,
    "--bs-drawer-color": GT.surfaceDark,
    "topBarHeading": darkThemeColor?.topBarHeading || GT.textStrongDark,
    /* The sidebar keeps its configured (coloured) background in dark mode, so
       these have to be light to stay legible on it — the previous #4d4b4e
       defaults sat at roughly 1.5:1 against the drawer. Any installation that
       has configured its own values in Setting → Theme Color still wins. */
    "drawerHeadingText": darkThemeColor?.drawerHeadingText || "#8FA3BC",
    "drawerMenuText": darkThemeColor?.drawerMenuText || "#CBD8E8",





    "--bs-topbar-item-color": GT.textMutedDark,
    "--bs-topbar-search-bg": GT.surfaceSunkenDark,
  },
  dataCardColorLight: {
    "--bs-heading-color": lightThemeColor?.heading || GT.textStrong,
    "--bs-card-bg-color": lightThemeColor?.cardBackground || GT.surface,
    /* Level-2 surface: the tinted band behind table headers, section strips and
       inner panels. Cool blue-grey now, never the inherited mint. */
    "--bs-card-sec-bg-color": lightThemeColor?.secondBackground || GT.surfaceSunken,
    "thirdBgColor": lightThemeColor?.thirdBgColor || GT.surfaceSunken,


    // "--bs-border-color": "#eaedf1",
    "--bs-table-hover-color": "#0F5CAB0A",
    "--bs-input-border-color": GT.border,
    "--bs-row-header-color": GT.surfaceSunken,
    "--bs-table-name-header-color": GT.textStrong,
  },
  dataCardColorDark: {
    "--bs-heading-color": darkThemeColor?.heading || GT.textStrongDark,
    "--bs-card-bg-color": darkThemeColor?.cardBackground || GT.surfaceDark,
    /* Dark mode was shipping the LIGHT mint (#eaf3f2) for both inner surfaces,
       which put near-white panels inside dark cards. Now a raised slate. */
    "--bs-card-sec-bg-color": darkThemeColor?.secondBackground || GT.surfaceSunkenDark,
    "thirdBgColor": darkThemeColor?.thirdBgColor || GT.surfaceSunkenDark,


    // "--bs-border-color":"#000000",
    "--bs-table-hover-color": "#FFFFFF08",
    "--bs-input-border-color": GT.borderDark,
    "--bs-row-header-color": GT.surfaceSunkenDark,
    "--bs-table-name-header-color": GT.textStrongDark
  },
  dataBsThemeLight: {
    "--bs-primary": lightThemeColor?.primary || GT.blue,
    "--bs-body-bg": lightThemeColor?.background || GT.page,
    "--bs-secondary": lightThemeColor?.secondary || GT.indigo,
    /* NOTE the inherited inversion, kept deliberately: `--bs-text-color` feeds
       palette.text.primary and is used across the app as the MUTED body tone,
       while `--bs-secondary-text` feeds text.secondary and is used for STRONG
       text. Swapping the roles would silently re-tone 195 files, so the
       redesign improves both values in place instead of renaming them. */
    "--bs-text-color": lightThemeColor?.textPrimary || GT.textMuted,
    "--bs-secondary-text": lightThemeColor?.textSecondary || GT.textStrong,
    "--bs-divider": lightThemeColor?.divider || "#d8dfe7", 
    "--bs-body-defult": GT.surface,












    "--bs-white": "#ffffff",
    "--bs-black": "#000000",
    "--bs-gray": "#5d7186",
    "--bs-gray-dark": "#36404a",
    "--bs-gray-100": "#f8f9fa",
    "--bs-gray-200": "#eef2f7",
    "--bs-gray-300": "#d8dfe7", 
    "--bs-gray-400": "#b0b0bb",
    "--bs-gray-500": "#8486a7",
    "--bs-gray-600": "#5d7186",
    "--bs-gray-700": "#424e5a",
    "--bs-gray-800": "#36404a",
    "--bs-gray-900": "#323a46",
    "--bs-success": GT.success,
    "--bs-warning": GT.warning,
    "--bs-info": GT.cyan,
    "--bs-danger": GT.danger,
    "--bs-purple": "#7f56da",
    "--bs-pink": "#ff86c8",
    "--bs-blue": GT.blue,
    "--bs-orange": "#ff6c2f",
    "--bs-indigo": "#53389f",
    "--bs-red": GT.danger,
    "--bs-yellow": GT.warning,
    "--bs-green": GT.success,
    "--bs-teal": "#040505",
    "--bs-cyan": GT.cyan,
    "--bs-light": "#eef2f7",
    "--bs-dark": "#323a46",
    "--bs-primary-rgb": "255, 108, 47",
    "--bs-secondary-rgb": "93, 113, 134",
    "--bs-success-rgb": "34, 197, 94",
    "--bs-warning-rgb": "249, 185, 49",
    "--bs-info-rgb": "78, 202, 194",
    "--bs-danger-rgb": "239, 95, 95",
    "--bs-purple-rgb": "127, 86, 218",
    "--bs-pink-rgb": "255, 134, 200",
    "--bs-blue-rgb": "28, 132, 238",
    "--bs-orange-rgb": "255, 108, 47",
    "--bs-indigo-rgb": "83, 56, 159",
    "--bs-red-rgb": "239, 95, 95",
    "--bs-yellow-rgb": "249, 185, 49",
    "--bs-green-rgb": "34, 197, 94",
    "--bs-teal-rgb": "4, 5, 5",
    "--bs-cyan-rgb": "78, 202, 194",
    "--bs-light-rgb": "238, 242, 247",
    "--bs-dark-rgb": "50, 58, 70",
    "--bs-primary-text-emphasis": "#662b13",
    "--bs-success-text-emphasis": "#0e4f26",
    "--bs-info-text-emphasis": "#1f514e",
    "--bs-warning-text-emphasis": "#644a14",
    "--bs-danger-text-emphasis": "#602626",
    "--bs-light-text-emphasis": "#424e5a",
    "--bs-dark-text-emphasis": "#424e5a",
    "--bs-primary-bg-subtle": "#ffe2d5",
    "--bs-secondary-bg-subtle": "#dfe3e7",
    "--bs-success-bg-subtle": "#d3f3df",
    "--bs-info-bg-subtle": "#dcf4f3",
    "--bs-warning-bg-subtle": "#fef1d6",
    "--bs-danger-bg-subtle": "#fcdfdf",
    "--bs-light-bg-subtle": "#fcfcfd",
    "--bs-dark-bg-subtle": "#b0b0bb",
    "--bs-primary-border-subtle": "#ffc4ac",
    "--bs-secondary-border-subtle": "#bec6cf",
    "--bs-success-border-subtle": "#a7e8bf",
    "--bs-info-border-subtle": "#b8eae7",
    "--bs-warning-border-subtle": "#fde3ad",
    "--bs-danger-border-subtle": "#f9bfbf",
    "--bs-light-border-subtle": "#eef2f7",
    "--bs-dark-border-subtle": "#8486a7",
    "--bs-white-rgb": "255, 255, 255",
    "--bs-black-rgb": "0, 0, 0",
    "--bs-font-sans-serif": '"Play", sans-serif',
    "--bs-font-monospace":
      'SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    "--bs-gradient":
      "linear-gradient(180deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0))",
    "--bs-body-font-family": "var(--bs-font-sans-serif)",
    "--bs-body-font-size": "0.875rem",
    "--bs-body-font-weight": "400",
    "--bs-body-line-height": "1.5",
    "--bs-body-color-rgb": "93, 113, 134",
    "--bs-body-bg-rgb": "249, 247, 247",
    "--bs-emphasis-color": "rgba(93, 113, 134, 0.75)",
    "--bs-emphasis-color-rgb": "93, 113, 134",
    "--bs-secondary-color": "#5d7186",
    "--bs-secondary-color-rgb": "93, 113, 134",
    "--bs-secondary-bg": "#ffffff",
    "--bs-secondary-bg-rgb": "255, 255, 255",
    "--bs-tertiary-color": "#424e5a",
    "--bs-tertiary-color-rgb": "66, 78, 90",
    "--bs-tertiary-bg": "#f8f9fa",
    "--bs-tertiary-bg-rgb": "248, 249, 250",
    "--bs-heading-color": "#aab8c5",
    "--bs-link-color": "#8486a7",
    "--bs-link-color-rgb": "132, 134, 167",
    "--bs-link-decoration": "none",
    "--bs-link-hover-color": "#d95c28",
    "--bs-link-hover-color-rgb": "217, 92, 40",
    "--bs-link-hover-decoration": "underline",
    "--bs-font-sans-serif-rgb": "30, 34, 36",
  },
  dataBsThemeDark: {
    /* Dark mode leads with the cyan accent: the deep brand blue does not carry
       enough luminance against a navy page to read as the primary action. */
    "--bs-primary": darkThemeColor?.primary || GT.cyan,
    "--bs-divider": darkThemeColor?.divider || GT.borderDark,
    "--bs-secondary-text": darkThemeColor?.textSecondary || GT.textStrongDark,
    "--bs-text-color": darkThemeColor?.textPrimary || GT.textMutedDark,
    "--bs-secondary": darkThemeColor?.secondary || GT.indigoLight,
    "--bs-body-bg": darkThemeColor?.background || GT.pageDark,




    "--bs-white": "#000000",
    "--bs-black": "#ffffff",
    "--bs-gray": "#8c9cb7",
    "--bs-gray-dark": "#6d767f",
    "--bs-gray-100": "#2d2d2d",
    "--bs-gray-200": "#3e3e3e",
    "--bs-gray-300": "#6b6b6b",
    "--bs-gray-400": "#9c9c9c",
    "--bs-gray-500": "#c2c3c8",
    "--bs-gray-600": "#c9c9c9",
    "--bs-gray-700": "#e0e0e0",
    "--bs-gray-800": "#e3e3e3",
    "--bs-gray-900": "#f1f1f1",
    "--bs-success": GT.successDark,
    "--bs-warning": GT.warningDark,
    "--bs-info": GT.cyan,
    "--bs-danger": GT.dangerDark,
    "--bs-purple": "#7f56da",
    "--bs-pink": "#ff86c8",
    "--bs-blue": GT.cyan,
    "--bs-orange": "#ff6c2f",
    "--bs-indigo": "#53389f",
    "--bs-red": GT.dangerDark,
    "--bs-yellow": GT.warningDark,
    "--bs-green": GT.successDark,
    "--bs-teal": "#040505",
    "--bs-cyan": GT.cyan,
    "--bs-light": "#282828",
    "--bs-dark": "#f9f9f9",
    "--bs-primary-rgb": "255, 108, 47",
    "--bs-secondary-rgb": "140, 156, 183",
    "--bs-success-rgb": "34, 197, 94",
    "--bs-warning-rgb": "249, 185, 49",
    "--bs-info-rgb": "78, 202, 194",
    "--bs-danger-rgb": "239, 95, 95",
    "--bs-purple-rgb": "127, 86, 218",
    "--bs-pink-rgb": "255, 134, 200",
    "--bs-blue-rgb": "28, 132, 238",
    "--bs-orange-rgb": "255, 108, 47",
    "--bs-indigo-rgb": "83, 56, 159",
    "--bs-red-rgb": "239, 95, 95",
    "--bs-yellow-rgb": "249, 185, 49",
    "--bs-green-rgb": "34, 197, 94",
    "--bs-teal-rgb": "4, 5, 5",
    "--bs-cyan-rgb": "78, 202, 194",
    "--bs-light-rgb": "40, 40, 40",
    "--bs-dark-rgb": "249, 249, 249",
    "--bs-primary-text-emphasis": "#f2d1c6",
    "--bs-success-text-emphasis": "#a4d4a4",
    "--bs-info-text-emphasis": "#a4d4d4",
    "--bs-warning-text-emphasis": "#f2c2a4",
    "--bs-danger-text-emphasis": "#f2a4a4",
    "--bs-light-text-emphasis": "#b3b3b3",
    "--bs-dark-text-emphasis": "#b3b3b3",
    "--bs-primary-bg-subtle": "#8c5c2f",
    "--bs-secondary-bg-subtle": "#6d4b2f",
    "--bs-success-bg-subtle": "#a2d3a2",
    "--bs-info-bg-subtle": "#a2d3d3",
    "--bs-warning-bg-subtle": "#f2a76c",
    "--bs-danger-bg-subtle": "#f2a4a4",
    "--bs-light-bg-subtle": "#1e1e1e",
    "--bs-dark-bg-subtle": "#383838",
    "--bs-primary-border-subtle": "#7f4c2f",
    "--bs-secondary-border-subtle": "#5d3f2f",
    "--bs-success-border-subtle": "#7f9a7f",
    "--bs-info-border-subtle": "#7f9a9a",
    "--bs-warning-border-subtle": "#7f5a3f",
    "--bs-danger-border-subtle": "#7f3f3f",
    "--bs-light-border-subtle": "#2c2c2c",
    "--bs-dark-border-subtle": "#4c4c4c",
    "--bs-white-rgb": "0, 0, 0",
    "--bs-black-rgb": "255, 255, 255",
    "--bs-font-sans-serif": '"Play", sans-serif',
    "--bs-font-monospace":
      'SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    "--bs-gradient":
      "linear-gradient(180deg, rgba(0, 0, 0, 0.15), rgba(0, 0, 0, 0))",
    "--bs-body-font-family": "var(--bs-font-sans-serif)",
    "--bs-body-font-size": "0.875rem",
    "--bs-body-font-weight": "400",
    "--bs-body-line-height": "1.5",
    "--bs-body-color-rgb": "140, 156, 183",
    "--bs-body-bg-rgb": "40, 40, 40",
    "--bs-emphasis-color": "rgba(140, 156, 183, 0.75)",
    "--bs-emphasis-color-rgb": "140, 156, 183",
    "--bs-secondary-color": "#8c9cb7",
    "--bs-secondary-color-rgb": "140, 156, 183",
    "--bs-secondary-bg": "#000000",
    "--bs-secondary-bg-rgb": "0, 0, 0",
    "--bs-tertiary-color": "#6d767f",
    "--bs-tertiary-color-rgb": "109, 118, 127",
    "--bs-tertiary-bg": "#1e1e1e",
    "--bs-tertiary-bg-rgb": "30, 30, 30",
    "--bs-heading-color": "#aab8c5",
    "--bs-link-color": "#c2c3c8",
    "--bs-link-color-rgb": "194, 195, 200",
    "--bs-link-decoration": "none",
    "--bs-link-hover-color": "#e5a4a4",
    "--bs-link-hover-color-rgb": "229, 164, 164",
    "--bs-link-hover-decoration": "underline",
    "--bs-font-sans-serif-rgb": "30, 34, 36",
  },
};
